/**
 * The Browse-by-Suburb carousel's paging arithmetic, extracted so it can be tested.
 *
 * ⚠️ WHY IT LIVES HERE (group C2, operator-ruled 2026-08-14). This maths used to sit
 * inline inside `scrollSuburbToPage`, one line above a `scrollTo` call — and the test
 * environment reports every layout offset as zero and implements no scrolling, so
 * driving that function proved nothing. The arithmetic is pure; only the final scroll
 * needs a browser. Splitting them lets the part that can carry an off-by-one be
 * tested, and leaves the part that cannot be tested honestly untested rather than
 * covered by a test that asserts a stub.
 *
 * ⚠️ THIS CONTROL HAS ALREADY SHIPPED ONE FAULT OF EXACTLY THIS CLASS: a hard-coded
 * three-dot row authored for a 6-column strip, which went silently wrong when a
 * second state doubled the columns to 12 — six pages, three dots, the last three
 * unreachable. That was the DOT COUNT (now guarded); this is the TARGET INDEX, the
 * same arithmetic from the other end.
 */

export interface PageTarget {
    /** The requested page after clamping into range. */
    page: number;
    /** Index of the column that should sit at the left edge of that page. */
    index: number;
}

/**
 * Where should the strip scroll to for a requested page?
 *
 * Returns `null` when there is nothing to scroll to — no columns, or a page size of
 * zero or less. The caller must treat `null` as "do nothing", which is what the
 * component's original `if (!first || !target) return` did.
 *
 * ⚠️ CLAMPS rather than rejects an out-of-range page, deliberately: the arrows call
 * this with `active - 1` and `active + 1` without checking the ends themselves, so
 * clamping here is what stops a press at either end from targeting a column that does
 * not exist. Do not "simplify" this into a bare multiply.
 */
export const pageTargetIndex = (
    columnCount: number,
    perPage: number,
    requestedPage: number,
): PageTarget | null => {
    if (!Number.isFinite(columnCount) || !Number.isFinite(perPage) || !Number.isFinite(requestedPage)) return null;
    if (columnCount <= 0 || perPage <= 0) return null;

    const maxPage = Math.ceil(columnCount / perPage) - 1;
    const page = Math.min(Math.max(Math.trunc(requestedPage), 0), maxPage);
    const index = page * perPage;

    // A clamped page can still address a column that exists, by construction — but
    // assert it rather than assume, because this is the exact spot an off-by-one in
    // `maxPage` would surface.
    if (index < 0 || index >= columnCount) return null;

    return { page, index };
};

/**
 * How many pages does the strip have? The DOT ROW consumes this.
 *
 * ⚠️ Floors at 1 where `pageTargetIndex` returns null for an empty strip. That
 * asymmetry is deliberate and pre-existing: a carousel with no columns still renders
 * one (empty) page's worth of dots rather than none, while there is nothing to scroll
 * TO. Kept identical to the component's existing `Math.max(1, …)` so extracting this
 * changed no behaviour.
 */
export const pageCount = (columnCount: number, perPage: number): number => {
    if (!Number.isFinite(columnCount) || !Number.isFinite(perPage) || perPage <= 0) return 1;
    return Math.max(1, Math.ceil(Math.max(columnCount, 0) / perPage));
};
